import type { AccountSummary, Provider } from '@fruitful/types';
import { useAtom } from 'jotai';
import * as React from 'react';
import {
    CheckIcon,
    Icon,
    isWeb,
    KeyboardCompatibleBottomSheet,
    useToastController,
    View,
} from 'smoothie';

import { captureException, captureMessage } from '../../../logger/Logger';
import useCreateGoalAccountLink from '../../hooks/useCreateGoalAccountLink';
import useGetGoals from '../../hooks/useGetGoals';
import { selectedGoalAtom } from '../../state/GoalAtoms';
import { ACCOUNT_LINKING_TOAST_DELAY } from '../../types/constants';
import type { IGoal } from '../../types/Goal';
import AccountList from './AccountList';

interface CreateGoalAccountLinkSheetProps {
    goalId: string;
    onDismiss: () => void;
}

const CreateGoalAccountLinkSheet = ({
    goalId,
    onDismiss,
}: CreateGoalAccountLinkSheetProps) => {
    const toast = useToastController();
    const [selectedGoal, setSelectedGoal] = useAtom(selectedGoalAtom);

    const { mutate: createGoalAccountLink } = useCreateGoalAccountLink();
    const { refetch: refetchGoals } = useGetGoals();

    const onLinkAccount = (accountSummary: AccountSummary) => {
        const { id: accountId, provider, institutionName } = accountSummary;

        createGoalAccountLink(
            {
                body: {
                    goalId,
                    accountId,
                    provider: provider.toUpperCase() as Provider,
                },
            },
            {
                onSuccess(data) {
                    // update the goal details page UI immediately
                    const updatedGoal = {
                        ...(selectedGoal as IGoal),
                        accountLinks: [data],
                    };
                    setSelectedGoal(updatedGoal);
                    void refetchGoals();

                    onDismiss();

                    setTimeout(
                        () =>
                            toast.show('Account linked', {
                                duration: 2000,
                                description: `Your ${institutionName} account has been linked`,
                                icon: (
                                    <View
                                        width={48}
                                        height={48}
                                        alignItems="center"
                                        justifyContent="center"
                                        backgroundColor="$green1000"
                                        borderRadius={8}
                                    >
                                        <Icon
                                            icon={CheckIcon}
                                            size={24}
                                            color="$white"
                                        />
                                    </View>
                                ),
                            }),
                        ACCOUNT_LINKING_TOAST_DELAY,
                    );
                },
                onError(e) {
                    // TODO: show toast?
                    captureMessage('Error creating goal account link');
                    captureException(e, {
                        goalId,
                        accountId,
                        provider,
                    });
                },
            },
        );
    };

    return (
        <KeyboardCompatibleBottomSheet
            onDismiss={onDismiss}
            enablePanDownToClose={!isWeb}
            enableContentPanningGesture={!isWeb}
            showCloseButton={false}
        >
            <AccountList
                onSelectAccount={(accountSummary) => {
                    onLinkAccount(accountSummary);
                }}
                onDismiss={onDismiss}
            />
        </KeyboardCompatibleBottomSheet>
    );
};

export default CreateGoalAccountLinkSheet;
